import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { PortalApiService } from "./../../../shared/services/api/portal-api.service";
import { CommonApiService } from "./../../../shared/services/api/common-api.service";
import { PractitionerMocksService } from "./../../../shared/services/mocks/practitioner-mocks.service";
import { UserService } from "./../../../shared/services/user.service";
import { CertificationPayment } from "./../../shared/models/payments/certification-payment";
import { ExamPayment } from "./../../shared/models/payments/exam-payment";
import { ProductsPayment } from "./../../shared/models/payments/products-payment";
import { RenewalPayment } from "./../../shared/models/payments/renewal-payment";
import { finalize } from "rxjs/operators";
import { environment } from "environments/environment";

const apiEndpoints = {
  ccpInfo: "ccp/{personId}",
  ccpPayment: "ccp/{personId}",
  credentials: "practitioner/{personId}/credentials",
  profile: "practitioner/{personId}/profile",
  renewal: "practitioner/{personId}/renewalInfo",
  purchase: "transactions/{personId}/purchase",
  examApplication: "practitioner/{personId}/examApplication",
  birthday: "practitioner/{personId}/birthday",
  charities: "openlookup/getCharities/{renewalYear}",
  setWait: "practitioner/wait/{personId}/{examId}/{eligDate}",
  payNow: "practitioneranon",
  purchasePayNow: "practitioneranon/{personId}/purchase",
};

@Injectable()
export class PurchaseService {
  constructor(
    private portalApiService: PortalApiService,
    private commonApiService: CommonApiService,
    private userService: UserService,
    private practitionerMocksService: PractitionerMocksService
  ) {}

  loadAcceptJs(): Promise<void> {
    return new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[src="' + environment.acceptJs + '"]'
      );
      if (existingScript) {
        resolve(); // Already loaded
        return;
      }

      const script = document.createElement("script");
      script.src = environment.acceptJs; //'https://js.authorize.net/v1/Accept.js';
      script.type = "text/javascript";
      script.async = true;

      script.onload = () => {
        //console.log("Accept.js loaded");
        resolve();
      };

      script.onerror = () => reject("Failed to load Accept.js");

      document.body.appendChild(script);
    });
  }

  getCCPInfo(personId: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.getCCPInfo()
      );
    }

    return this.portalApiService
      .get(apiEndpoints.ccpInfo.replace("{personId}", personId))
      .pipe(finalize(() => null));
  }

  getCharities(renewalYear): Observable<any> {
    return this.commonApiService.get(
      apiEndpoints.charities.replace("{renewalYear}", renewalYear)
    );
  }

  getCredentials(personId: string): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.getCredentialsAndExams().credentials
      );}

    return this.portalApiService.get(
      apiEndpoints.credentials.replace("{personId}", personId)
    );
  }

  getProfile(personId: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      this.practitionerMocksService.profile;
    }

    return this.portalApiService.get(
      apiEndpoints.profile.replace("{personId}", personId)
    );
  }

  getRenewalInfo(personId: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      this.practitionerMocksService.getRenewalInfo();
    }

    return this.portalApiService.get(
      apiEndpoints.renewal.replace("{personId}", personId)
    );
  }

  getExamApplicationInfo(
    personId: string,
    examCode: string,    
  ): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.getExamApplication()
      );
    }

    const params: any = {
      personId: personId,
      examCode: examCode,
      readHandbook: true,
    };

    return this.portalApiService.post(
      apiEndpoints.examApplication.replace("{personId}", personId),
      params
    );
  }

  getPayNow(guid: string, email: string, source = ""): Observable<any> {
    const x = apiEndpoints.payNow.concat(
      `/${guid}/paynow?x=${email}&source=${source}`
    );
    return this.portalApiService.get(x);
  }

  sendRenewalPaymentPayNow(
    renewalPayment: RenewalPayment,
    source = ""
  ): Observable<any> {
    return this.portalApiService
      .post(
        apiEndpoints.purchasePayNow.replace(
          "{personId}",
          renewalPayment.personID.toString()
        ) +
          "?source=" +
          source,
        renewalPayment
      )
      .pipe(finalize(() => null));
  }

  sendCertificationPayment(
    certificationPayment: CertificationPayment
  ): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.getExamPurchase()
      );}

    return this.portalApiService
      .post(
        apiEndpoints.ccpPayment.replace(
          "{personId}",
          certificationPayment.personId.toString()
        ),
        certificationPayment
      )
      .pipe(finalize(() => null));
  }

  sendExamPayment(examPayment: ExamPayment): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.getExamPurchase()
      );}

    return this.portalApiService
      .post(
        apiEndpoints.purchase.replace(
          "{personId}",
          examPayment.personId.toString()
        ),
        examPayment
      )
      .pipe(finalize(() => null));
  }

  sendProductsPayment(productsPayment: ProductsPayment): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.submitCCPayment()
      );}

    return this.portalApiService
      .post(
        apiEndpoints.purchase.replace(
          "{personId}",
          productsPayment.personID.toString()
        ),
        productsPayment
      )
      .pipe(finalize(() => null));
  }

  sendRenewalPayment(renewalPayment: RenewalPayment): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.submitCCPayment()
      );}

    return this.portalApiService
      .post(
        apiEndpoints.purchase.replace(
          "{personId}",
          renewalPayment.personID.toString()
        ),
        renewalPayment
      )
      .pipe(finalize(() => null));
  }

  sendWaitNotice(personId, examId, eligDate): Observable<any> {
    return this.portalApiService
      .post(
        apiEndpoints.setWait
          .replace("{personId}", personId)
          .replace("{examId}", examId)
          .replace("{eligDate}", eligDate)
      )
      .pipe(finalize(() => null));
  }

  updateDateOfBirth(personId: string, dob: string): Observable<any> {
    const dateOfBirth = { Birthday: dob };
    return this.portalApiService
      .put(apiEndpoints.birthday.replace("{personId}", personId), dateOfBirth)
      .pipe(finalize(() => null));
  }
}
